<script lang="ts" setup>
import { nextTick, onMounted, ref, watch } from "vue";

const props = defineProps({
  tabs: {
    type: Array as PropType<Tabs>,
    required: true,
  },
  modelValue: {
    type: String,
  },
});

const emit = defineEmits(["update:modelValue"]);

import type { Ref } from "vue";

const tabRefs: Ref<Array<HTMLElement | null>> = ref([]);
const indicatorStyle = ref({ left: "0px", width: "0px" });

const selectTab = (key: string) => {
  emit("update:modelValue", key);
};

const updateIndicator = () => {
  const idx = props.tabs.findIndex((tab) => tab.key === props.modelValue);
  const el = tabRefs.value[idx];
  if (idx !== -1 && el) {
    const parentRect = el.parentElement?.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    if (parentRect) {
      indicatorStyle.value = {
        left: `${rect.left - parentRect.left}px`,
        width: `${rect.width}px`,
      };
    }
  }
};

import { onBeforeUnmount, ref as vueRef } from "vue";

const tabBarRef = vueRef<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | null = null;
const isResizing = ref(false);
let resizeTimeout: number | null = null;

onMounted(() => {
  nextTick(updateIndicator);
  if (tabBarRef.value) {
    resizeObserver = new ResizeObserver(() => {
      isResizing.value = true;
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        isResizing.value = false;
      }, 200);
      nextTick(() => {
        requestAnimationFrame(() => {
          updateIndicator();
        });
      });
    });
    resizeObserver.observe(tabBarRef.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver && tabBarRef.value) {
    resizeObserver.unobserve(tabBarRef.value);
    resizeObserver.disconnect();
  }
});

watch(
  () => props.modelValue,
  async () => {
    await nextTick();
    updateIndicator();
  }
);

watch(
  () => props.tabs,
  async () => {
    await nextTick();
    updateIndicator();
  }
);

import type { ComponentPublicInstance } from "vue";

const setTabRef = (el: Element | ComponentPublicInstance | null, i: number) => {
  tabRefs.value[i] = el instanceof HTMLElement ? el : null;
};
</script>

<template>
  <div
    ref="tabBarRef"
    class="relative flex flex-row rounded p-1 bg-light-default overflow-hidden"
  >
    <!-- Animated indicator -->
    <div
      :class="[
        'absolute top-0 bottom-0 rounded bg-surface shadow z-0 my-1',
        !isResizing && 'transition-all duration-300',
      ]"
      :style="indicatorStyle"
    ></div>
    <span
      v-for="(tab, i) in tabs"
      :key="tab.key"
      :data-step-id="tab.stepId"
      class="relative grow text-center cursor-pointer rounded p-2 z-10 select-none text-input-1 flex flex-row items-center justify-center"
      @click="selectTab(tab.key)"
      :ref="(el) => setTabRef(el, i)"
      :class="{}"
    >
      <icon v-if="tab.icon" :name="tab.icon" class="mr-1" />
      {{ tab.label }}
      <div
        v-if="tab.badge"
        class="absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-danger-default text-[10px] font-bold text-danger-on shadow-sm badge-pulse"
      >
        {{ tab.badge }}
      </div>
    </span>
  </div>
</template>

<style scoped>
@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 0 0 rgba(161, 0, 0, 0.4);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(161, 0, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(161, 0, 0, 0);
  }
}

.badge-pulse {
  animation: pulse-glow 2s infinite;
}
</style>
